import { useState } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Power, PowerOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AddEntityTypeDialog } from "@/components/AddEntityTypeDialog";
import { EditEntityTypeDialog } from "@/components/EditEntityTypeDialog";

interface EntityType {
  id: string;
  name: string;
  label: string;
  color: string;
  is_active: boolean;
  created_at: string;
}

interface ContactCount {
  entity_type: string;
  count: number;
}

const colorVariants: Record<string, string> = {
  blue: "bg-blue-500",
  green: "bg-green-500",
  purple: "bg-purple-500",
  orange: "bg-orange-500",
  red: "bg-red-500",
  yellow: "bg-yellow-500",
};

export default function EntityTypeManagement() {
  const { toast } = useToast();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedType, setSelectedType] = useState<EntityType | null>(null);
  const [typeToDelete, setTypeToDelete] = useState<EntityType | null>(null);

  // Fetch entity types
  const { data: entityTypes, refetch: refetchTypes } = useQuery({
    queryKey: ["entity-types-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("entity_types")
        .select("*")
        .order("label");
      
      if (error) throw error;
      return data as EntityType[];
    },
  });

  // Fetch contact counts per type
  const { data: contactCounts } = useQuery({
    queryKey: ["contact-counts-by-type"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contacts")
        .select("entity_type");
      
      if (error) throw error;
      
      const counts = data.reduce((acc, contact) => {
        const type = contact.entity_type;
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      return counts;
    },
  });

  const getContactCount = (typeName: string) => {
    return contactCounts?.[typeName] || 0;
  };

  const handleToggleActive = async (type: EntityType) => {
    const contactCount = getContactCount(type.name);
    
    // Não permitir desativar se houver contatos usando
    if (type.is_active && contactCount > 0) {
      toast({
        title: "Não é possível desativar",
        description: `${contactCount} contatos ainda usam esse tipo.`,
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from("entity_types")
        .update({ is_active: !type.is_active })
        .eq("id", type.id);

      if (error) throw error;

      toast({
        title: type.is_active ? "Tipo desativado" : "Tipo ativado",
        description: `"${type.label}" foi ${type.is_active ? 'desativado' : 'ativado'}.`,
      });

      refetchTypes();
    } catch (error) {
      console.error("Error toggling type:", error);
      toast({
        title: "Erro",
        description: "Não foi possível alterar o status do tipo.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteClick = (type: EntityType) => {
    const contactCount = getContactCount(type.name);
    
    if (contactCount > 0) {
      toast({
        title: "Não é possível deletar",
        description: `${contactCount} contatos ainda usam esse tipo. Reatribua os contatos primeiro.`,
        variant: "destructive",
      });
      return;
    }

    setTypeToDelete(type);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!typeToDelete) return;

    try {
      const { error } = await supabase
        .from("entity_types")
        .delete()
        .eq("id", typeToDelete.id);

      if (error) throw error;

      toast({
        title: "Tipo deletado",
        description: `"${typeToDelete.label}" foi removido.`,
      });

      refetchTypes();
      setShowDeleteDialog(false);
      setTypeToDelete(null);
    } catch (error) {
      console.error("Error deleting type:", error);
      toast({
        title: "Erro",
        description: "Não foi possível deletar o tipo.",
        variant: "destructive",
      });
    }
  };

  const activeTypes = entityTypes?.filter(t => t.is_active).length || 0;
  const inUseTypes = entityTypes?.filter(t => getContactCount(t.name) > 0).length || 0;

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar onClearData={() => {}} />
        <main className="flex-1 p-8">
          <div className="max-w-6xl mx-auto space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Tipos de Empresa</h1>
                <p className="text-muted-foreground mt-1">
                  Gerencie os tipos de empresa usados para classificar contatos
                </p>
              </div>
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Novo Tipo
              </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total de Tipos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{entityTypes?.length || 0}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Tipos Ativos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{activeTypes}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Tipos em Uso</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{inUseTypes}</div>
                </CardContent>
              </Card>
            </div>

            {/* Types Table */}
            <Card>
              <CardHeader>
                <CardTitle>Tipos de Empresa</CardTitle>
                <CardDescription>
                  Lista completa de tipos disponíveis no sistema
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Badge</TableHead>
                      <TableHead>Nome Técnico</TableHead>
                      <TableHead>Nome de Exibição</TableHead>
                      <TableHead className="text-center">Contatos</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entityTypes?.map((type) => {
                      const contactCount = getContactCount(type.name);
                      return (
                        <TableRow key={type.id}>
                          <TableCell>
                            <div className={`w-4 h-4 rounded-full ${colorVariants[type.color] || colorVariants.blue}`} />
                          </TableCell>
                          <TableCell className="font-mono text-sm">{type.name}</TableCell>
                          <TableCell className="font-medium">{type.label}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant={contactCount > 0 ? "default" : "secondary"}>
                              {contactCount}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {type.is_active ? (
                              <Badge variant="default" className="bg-green-500">
                                Ativo
                              </Badge>
                            ) : (
                              <Badge variant="secondary">
                                Inativo
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedType(type);
                                  setShowEditDialog(true);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleActive(type)}
                                title={type.is_active ? "Desativar" : "Ativar"}
                              >
                                {type.is_active ? (
                                  <PowerOff className="h-4 w-4 text-orange-500" />
                                ) : (
                                  <Power className="h-4 w-4 text-green-500" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClick(type)}
                                disabled={contactCount > 0}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>

      <AddEntityTypeDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onSuccess={refetchTypes}
      />

      <EditEntityTypeDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        entityType={selectedType}
        contactCount={selectedType ? getContactCount(selectedType.name) : 0}
        onSuccess={refetchTypes}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deletar Tipo de Empresa</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja deletar "{typeToDelete?.label}"? Esta ação não pode ser desfeita.
              {['restaurant', 'operator', 'consolidator'].includes(typeToDelete?.name || '') && (
                <span className="block mt-2 font-semibold text-destructive">
                  ⚠️ Este é um tipo padrão do sistema!
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  );
}
